import { useApi } from "../api";

export const useScheduleService = () => {
  const api = useApi();

  return {
    /**
     * Obtener programación para un rango de fechas
     */
    getScheduleByDateRange: async (
      operationId,
      startDate,
      endDate,
      options = {}
    ) => {
      try {
        const params = {
          startDate,
          endDate,
          operationRouteId: options.operationRouteId,
        };

        const response = await api.get(
          `/vehicle/operation/${operationId}/schedule`,
          { params }
        );
        return response.data;
      } catch (error) {
        console.error("Error obteniendo programación:", error);
        throw error.response?.data || { message: error.message };
      }
    },
    /**
     * Añadir vehículo a programación
     */
    addVehicleToSchedule: async (
      operationId,
      vehicleId,
      date,
      position,
      operationRouteId
    ) => {
      try {
        const response = await api.post(
          `/vehicle/operation/${operationId}/schedule`,
          {
            vehicleId,
            date,
            position,
            operationRouteId,
          }
        );
        return response.data;
      } catch (error) {
        console.error("Error añadiendo vehículo a programación:", error);
        throw error.response?.data || { message: error.message };
      }
    },
    /**
     * Obtener vehículos disponibles por fecha y ruta
     */
    getAvailableVehiclesByDate: async (operationId, date, options = {}) => {
      try {
        const {
          vehicleType = "all",
          search = "",
          operationRouteId = null,
        } = options;

        const params = new URLSearchParams({
          vehicleType,
          search: search || "",
          ...(operationRouteId && { operationRouteId }),
        });

        const response = await api.get(
          `/vehicle/operation/${operationId}/available-vehicles/${date}?${params.toString()}`
        );
        return response.data;
      } catch (error) {
        console.error("Error obteniendo vehículos disponibles:", error);
        throw error.response?.data || { message: error.message };
      }
    },
    /**
     * Reordenar vehículos en un día
     */
    reorderVehicles: async (operationId, date, vehicles) => {
      try {
        const response = await api.put(
          `/vehicle/operation/${operationId}/schedule/reorder`,
          {
            date,
            vehicles,
          }
        );
        return response.data;
      } catch (error) {
        console.error("Error reordenando vehículos:", error);
        throw error.response?.data || { message: error.message };
      }
    },
    /**
     * Mover vehículo entre días
     */
    moveVehicleToDay: async (scheduleId) => {
      const { scheduleId: id, newDate, newPosition } = scheduleId;
      try {
        const response = await api.put(`/vehicle/schedule/${id}/move`, {
          newDate,
          newPosition,
        });
        return response.data;
      } catch (error) {
        console.error("Error moviendo vehículo:", error);
        throw error.response?.data || { message: error.message };
      }
    },
    /**
     * Eliminar vehículo de programación
     */
    removeVehicleFromSchedule: async (scheduleId) => {
      try {
        const response = await api.delete(`/vehicle/schedule/${scheduleId}`);
        return response.data;
      } catch (error) {
        console.error("Error eliminando vehículo de programación:", error);
        throw error.response?.data || { message: error.message };
      }
    },
    /**
     * Obtener eventos por rango de fechas con soporte de rutas
     */
    getEventsByDateRange: async (
      operationId,
      startDate,
      endDate,
      options = {}
    ) => {
      try {
        const params = {
          startDate,
          endDate,
        };

        // Incluir operationRouteId solo si está presente
        if (options.operationRouteId) {
          params.operationRouteId = options.operationRouteId;
        }

        const response = await api.get(
          `/vehicle/operation/${operationId}/events/range`,
          { params }
        );
        return response.data;
      } catch (error) {
        console.error("Error obteniendo eventos por rango:", error);
        throw error.response?.data || { message: error.message };
      }
    },
    /**
     * Añadir evento
     */
    addEvent: async (operationId, eventData) => {
      try {
        const payload = {
          ...eventData,
        };

        // Incluir operationRouteId solo si está presente
        if (eventData.operationRouteId) {
          payload.operationRouteId = eventData.operationRouteId;
        }

        const response = await api.post(
          `/vehicle/operation/${operationId}/events`,
          payload
        );
        return response.data;
      } catch (error) {
        console.error("Error añadiendo evento:", error);
        throw error.response?.data || { message: error.message };
      }
    },
    /**
     * Actualizar evento
     */
    updateEvent: async (eventId, eventData) => {
      try {
        const payload = {
          ...eventData,
        };

        // Incluir operationRouteId solo si está presente
        if (eventData.operationRouteId !== undefined) {
          payload.operationRouteId = eventData.operationRouteId;
        }

        const response = await api.put(`/vehicle/events/${eventId}`, payload);
        return response.data;
      } catch (error) {
        console.error("Error actualizando evento:", error);
        throw error.response?.data || { message: error.message };
      }
    },
    /**
     * Eliminar evento
     */
    deleteEvent: async (eventId) => {
      try {
        const response = await api.delete(`/vehicle/events/${eventId}`);
        return response.data;
      } catch (error) {
        console.error("Error eliminando evento:", error);
        throw error.response?.data || { message: error.message };
      }
    },
    /**
     * Generar programación basada en patrones
     */
    generateScheduleFromPattern: async (
      operationId,
      patternDays,
      startDate,
      endDate
    ) => {
      try {
        const response = await api.post(
          `/vehicle/operation/${operationId}/schedule/generate`,
          {
            patternDays,
            startDate,
            endDate,
          }
        );
        return response.data;
      } catch (error) {
        console.error("Error generando programación:", error);
        throw error.response?.data || { message: error.message };
      }
    },
    /**
     * Regenerar programación futura basada en patrones
     */
    regenerateFutureSchedule: async (operationId) => {
      try {
        const response = await api.post(
          `/vehicle/operation/${operationId}/schedule/regenerate`
        );
        return response.data;
      } catch (error) {
        console.error("Error regenerando programación futura:", error);
        throw error.response?.data || { message: error.message };
      }
    },
    /**
     * Cancelar vehículo programado
     */
    cancelScheduledVehicle: async (scheduleId, cancellationData) => {
      try {
        const response = await api.put(
          `/vehicle/schedule/${scheduleId}/cancel`,
          cancellationData
        );
        return response.data;
      } catch (error) {
        console.error("Error cancelando vehículo:", error);
        throw error.response?.data || { message: error.message };
      }
    },
    /**
     * Obtener capacidad mensual por ruta
     */
    getMonthlyCapacityByRoute: async (
      operationId,
      year,
      month,
      operationRouteId
    ) => {
      try {
        const response = await api.get(
          `/vehicle/operation/${operationId}/capacity`,
          {
            params: { year, month, operationRouteId },
          }
        );
        return response.data;
      } catch (error) {
        console.error("Error obteniendo capacidad mensual por ruta:", error);
        throw error.response?.data || { message: error.message };
      }
    },
    /**
     * Obtener capacidad mensual
     */
    getMonthlyCapacity: async (operationId, year, month) => {
      try {
        const response = await api.get(
          `/vehicle/operation/${operationId}/capacity`,
          {
            params: { year, month },
          }
        );
        return response.data;
      } catch (error) {
        console.error("Error obteniendo capacidad mensual:", error);
        throw error.response?.data || { message: error.message };
      }
    },
    /**
     * Establecer capacidad mensual por ruta
     */
    setMonthlyCapacityByRoute: async (
      operationId,
      year,
      month,
      capacities,
      operationRouteId
    ) => {
      try {
        const response = await api.put(
          `/vehicle/operation/${operationId}/capacity`,
          { year, month, capacities, operationRouteId }
        );
        return response.data;
      } catch (error) {
        console.error("Error estableciendo capacidad mensual por ruta:", error);
        throw error.response?.data || { message: error.message };
      }
    },
    /**
     * Establecer capacidad mensual
     */
    setMonthlyCapacity: async (operationId, year, month, capacities) => {
      try {
        const response = await api.put(
          `/vehicle/operation/${operationId}/capacity`,
          { year, month, capacities }
        );
        return response.data;
      } catch (error) {
        console.error("Error estableciendo capacidad mensual:", error);
        throw error.response?.data || { message: error.message };
      }
    },
    /**
     * Obtener estado de capacidad con filtro de ruta
     */
    getCapacityStatusByRoute: async (
      operationId,
      startDate,
      endDate,
      operationRouteId
    ) => {
      try {
        const response = await api.get(
          `/vehicle/operation/${operationId}/capacity/status`,
          {
            params: { startDate, endDate, operationRouteId },
          }
        );
        return response.data;
      } catch (error) {
        console.error("Error obteniendo estado de capacidad por ruta:", error);
        throw error.response?.data || { message: error.message };
      }
    },
    /**
     * Obtener estado de capacidad
     */
    getCapacityStatus: async (operationId, startDate, endDate) => {
      try {
        const response = await api.get(
          `/vehicle/operation/${operationId}/capacity/status`,
          {
            params: { startDate, endDate },
          }
        );
        return response.data;
      } catch (error) {
        console.error("Error obteniendo estado de capacidad:", error);
        throw error.response?.data || { message: error.message };
      }
    },
    /**
     * Obtener programación con capacidad
     */
    getScheduleWithCapacity: async (operationId, startDate, endDate) => {
      try {
        const response = await api.get(
          `/vehicle/operation/${operationId}/schedule-with-capacity`,
          {
            params: { startDate, endDate },
          }
        );
        return response.data;
      } catch (error) {
        console.error("Error obteniendo programación con capacidad:", error);
        throw error.response?.data || { message: error.message };
      }
    },
  };
};
